import { extractText, getDocumentProxy } from 'unpdf';
import mammoth from 'mammoth';

/** Match installed pdfjs-dist (see package-lock) so CMaps align with PDF.js. */
const PDF_CMAP_BASE_URL = "https://unpkg.com/pdfjs-dist@5.4.530/cmaps/";

export interface ExtractedText {
    text: string;
    filename: string;
    mimeType: string;
    error?: string;
}

/**
 * Extracts text from a blob based on its MIME type
 */
export async function extractTextFromBlob(
    blob: Blob,
    filename: string,
    mimeType: string,
    accessToken?: string
): Promise<ExtractedText> {
    try {
        // Google Workspace documents - need to be exported via API
        if (mimeType.startsWith('application/vnd.google-apps.')) {
            return await extractTextFromGoogleWorkspace(blob, filename, mimeType, accessToken);
        }

        const nameLower = filename.toLowerCase();
        const typeLower = mimeType.toLowerCase();
        // PDF: MIME, extension, or %PDF magic (fixes wrong type / storage ids without .pdf)
        const isPdf =
            typeLower === "application/pdf" ||
            nameLower.endsWith(".pdf") ||
            (await isPdfMagicHeader(blob));
        if (isPdf) {
            return await extractTextFromPDF(blob, filename);
        }

        // Microsoft Word documents (DOCX)
        if (
            mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            nameLower.endsWith(".docx")
        ) {
            return await extractTextFromDOCX(blob, filename);
        }

        // Legacy Word documents (DOC) - limited support
        if (mimeType === "application/msword" || nameLower.endsWith(".doc")) {
            return await extractTextFromDOC(blob, filename);
        }

        // Text files
        if (mimeType.startsWith('text/')) {
            return await extractTextFromPlainText(blob, filename);
        }

        // JSON files
        if (mimeType === "application/json" || nameLower.endsWith(".json")) {
            return await extractTextFromPlainText(blob, filename);
        }

        // CSV files
        if (mimeType === "text/csv" || nameLower.endsWith(".csv")) {
            return await extractTextFromPlainText(blob, filename);
        }

        // Markdown files
        if (nameLower.endsWith(".md") || nameLower.endsWith(".markdown")) {
            return await extractTextFromPlainText(blob, filename);
        }

        // Default: try to extract as plain text
        return await extractTextFromPlainText(blob, filename);
    } catch (error: any) {
        return {
            text: '',
            filename,
            mimeType,
            error: error.message || 'Failed to extract text from file',
        };
    }
}

/** True if the blob starts with the PDF signature %PDF */
async function isPdfMagicHeader(blob: Blob): Promise<boolean> {
    try {
        const header = await blob.slice(0, 4).arrayBuffer();
        const view = new Uint8Array(header);
        return (
            view[0] === 0x25 &&
            view[1] === 0x50 &&
            view[2] === 0x44 &&
            view[3] === 0x46
        );
    } catch {
        return false;
    }
}

/**
 * Extracts text from Google Workspace documents
 * Note: This requires the file to be exported via Google Drive API
 */
async function extractTextFromGoogleWorkspace(
    blob: Blob,
    filename: string,
    mimeType: string,
    accessToken?: string
): Promise<ExtractedText> {
    // For Google Workspace files, the blob should already be exported text
    // If it's not, we'll try to read it as text
    try {
        const text = await blob.text();
        // Preserve the original text formatting (line breaks, spacing)
        // Only trim excessive leading/trailing whitespace, but preserve structure
        return {
            text: text.trimEnd(), // Only trim trailing whitespace to preserve leading indentation
            filename,
            mimeType,
        };
    } catch (error: any) {
        return {
            text: '',
            filename,
            mimeType,
            error: 'Failed to extract text from Google Workspace document. Make sure the file is exported as text.',
        };
    }
}

/**
 * Extracts text from PDF files using unpdf (no worker required)
 * Preserves formatting including line breaks and spacing
 */
async function extractTextFromPDF(blob: Blob, filename: string): Promise<ExtractedText> {
    try {
        const data = new Uint8Array(await blob.arrayBuffer());

        // Open with CMaps so embedded fonts (e.g. Google Docs / Skia PDFs) decode correctly.
        // unpdf's extractText() does not pass cMapUrl into getDocument; use getDocumentProxy first.
        const pdf = await getDocumentProxy(data, {
            cMapUrl: PDF_CMAP_BASE_URL,
            cMapPacked: true,
        });
        const result = await extractText(pdf);
        
        // Join pages preserving formatting
        // Each page's text should preserve line breaks if the PDF has them
        // Don't trim individual pages to preserve leading/trailing whitespace that might be intentional
        // Only filter out completely empty pages
        const fullText = result.text
            .filter(pageText => pageText && pageText.trim().length > 0)
            .join('\n\n') // Double newline between pages
            .trimEnd(); // Only trim trailing whitespace
        
        // Note: unpdf's extractText may not always preserve exact formatting
        // If formatting is still lost, the PDF might not have explicit line break information
        // In that case, the text might need OCR or a different extraction method
        return {
            text: fullText,
            filename,
            mimeType: 'application/pdf',
        };
    } catch (error: any) {
        const errorMessage = error.message || 'Unknown error';
        
        return {
            text: '',
            filename,
            mimeType: 'application/pdf',
            error: `Failed to extract text from PDF: ${errorMessage}`,
        };
    }
}

/**
 * Extracts text from DOCX files using mammoth
 * Preserves formatting including line breaks and paragraphs
 */
async function extractTextFromDOCX(blob: Blob, filename: string): Promise<ExtractedText> {
    try {
        const arrayBuffer = await blob.arrayBuffer();
        
        // extractRawText preserves line breaks and paragraph structure
        // It converts paragraphs to double newlines and preserves line breaks within paragraphs
        const result = await mammoth.extractRawText({ arrayBuffer });
        
        // result.value should already preserve line breaks and formatting
        // mammoth.extractRawText preserves paragraph breaks as double newlines
        // Only trim trailing whitespace to preserve leading indentation and structure
        return {
            text: result.value.trimEnd(),
            filename,
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };
    } catch (error: any) {
        return {
            text: '',
            filename,
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            error: `Failed to extract text from DOCX: ${error.message}`,
        };
    }
}

const MAX_DOC_UPLOAD_BYTES = 12 * 1024 * 1024;

function resolveExtractDocUrl(): string {
    const origin = import.meta.env.VITE_API_ORIGIN;
    if (typeof origin === "string" && origin.trim()) {
        return `${origin.replace(/\/$/, "")}/api/extract-doc`;
    }
    return "/api/extract-doc";
}

async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result;
            if (typeof result !== "string") {
                reject(new Error("Failed to read file"));
                return;
            }
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
        reader.readAsDataURL(blob);
    });
}

/**
 * Legacy .doc: browser calls POST /api/extract-doc (Vercel serverless or local Express + Vite proxy).
 */
async function extractTextFromDOC(blob: Blob, filename: string): Promise<ExtractedText> {
    if (blob.size > MAX_DOC_UPLOAD_BYTES) {
        return {
            text: "",
            filename,
            mimeType: "application/msword",
            error: `File too large for .doc extraction (max ${MAX_DOC_UPLOAD_BYTES / (1024 * 1024)}MB). Convert to DOCX or PDF.`,
        };
    }

    try {
        const base64 = await blobToBase64(blob);
        const res = await fetch(resolveExtractDocUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ base64, filename }),
        });

        if (!res.ok) {
            const errBody = (await res.json().catch(() => ({}))) as { error?: string };
            const msg = errBody.error || res.statusText || "Server extraction failed";
            return {
                text: "",
                filename,
                mimeType: "application/msword",
                error: `${msg}. You can convert to DOCX or PDF and try again.`,
            };
        }

        const data = (await res.json()) as { text?: string };
        const text = (data.text ?? "").trim();
        if (!text) {
            return {
                text: "",
                filename,
                mimeType: "application/msword",
                error: "No text extracted from .doc. Try converting to DOCX or PDF.",
            };
        }

        return {
            text: text.trimEnd(),
            filename,
            mimeType: "application/msword",
        };
    } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Legacy DOC extraction failed";
        const hint =
            message === "Failed to fetch"
                ? "Could not reach .doc extraction (start the API server on port 3001 or use production). "
                : "";
        return {
            text: "",
            filename,
            mimeType: "application/msword",
            error: `${hint}${message}. Convert to DOCX or PDF if this persists.`,
        };
    }
}

/**
 * Extracts text from plain text files
 */
async function extractTextFromPlainText(blob: Blob, filename: string): Promise<ExtractedText> {
    try {
        const text = await blob.text();
        // Preserve original formatting for plain text files
        // Only trim trailing whitespace to preserve leading indentation and structure
        return {
            text: text.trimEnd(), // Preserve line breaks and spacing
            filename,
            mimeType: blob.type || 'text/plain',
        };
    } catch (error: any) {
        return {
            text: '',
            filename,
            mimeType: blob.type || 'text/plain',
            error: `Failed to read text file: ${error.message}`,
        };
    }
}

/**
 * Extracts text from multiple files
 */
export async function extractTextFromFiles(
    files: Array<{ blob: Blob; filename: string; mimeType: string; accessToken?: string }>
): Promise<ExtractedText[]> {
    const results = await Promise.all(
        files.map((file) =>
            extractTextFromBlob(file.blob, file.filename, file.mimeType, file.accessToken)
        )
    );
    
    return results;
}

